import { useState, useEffect } from 'react';
import { useOnboarding } from '../OnboardingContext';
import { Check, Shield, AlertCircle, FileText, Camera, Home, AlertTriangle, Loader2 } from 'lucide-react';
import { SumsubVerification, ExtractedData } from '../SumsubVerification';
import { VerificationConfirmScreen } from '../VerificationConfirmScreen';
import { supabase } from '@/integrations/supabase/client';

export function DocumentUploadStep() {
  const { data, updateData, goToNextStep, sessionToken } = useOnboarding();

  // Corporate flow: biometrics already done in UBO Trace step (Step 10).
  // Find the applicant's entry in corporateBiometrics and skip Sumsub entirely.
  const isCorporate = data.accountType === 'corporate';
  const corporateBiometrics = (data.corporateBiometrics || []) as any[];
  const applicantEntry = isCorporate
    ? (corporateBiometrics.find((b: any) => b.email === data.email || b.type === 'director') || corporateBiometrics[0])
    : null;
  const alreadyDoneInCorporate = isCorporate && applicantEntry?.completed;

  const [verified, setVerified] = useState(alreadyDoneInCorporate);
  const [verificationFailed, setVerificationFailed] = useState(false);
  const [failureReason, setFailureReason] = useState<string | null>(null);
  const [kycError, setKycError] = useState(false);
  const [showSumsub, setShowSumsub] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingExtracted, setPendingExtracted] = useState<ExtractedData | undefined>(undefined);
  const [enrichedEntry, setEnrichedEntry] = useState<any>(null);
  const [enriching, setEnriching] = useState(false);

  // Helper: resolve the DB session row id (mirrors CorporateUBOTraceStep logic)
  const getSessionDbId = async (): Promise<string | null> => {
    if (sessionToken) {
      const { data: byToken } = await supabase
        .from('onboarding_sessions')
        .select('id')
        .filter('onboarding_data->>session_token', 'eq', sessionToken)
        .maybeSingle();
      if (byToken?.id) return byToken.id;
    }
    if (data.email) {
      const { data: byEmail } = await supabase
        .from('onboarding_sessions')
        .select('id')
        .filter('onboarding_data->>email', 'eq', data.email)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (byEmail?.id) return byEmail.id;
    }
    return null;
  };

  // When corporate entry is complete but has no FULL KYC data (document fields), pull it live from Sumsub.
  // NOTE: firstName alone is NOT sufficient — it's set at registration time. We need documentNumber.
  useEffect(() => {
    if (!alreadyDoneInCorporate) return;
    const ae = applicantEntry as any;
    const hasFullKyc = ae?.documentNumber || ae?.confirmedData?.documentNumber;
    if (hasFullKyc) return;

    const personId = ae?.id;
    if (!personId) return;

    setEnriching(true);
    getSessionDbId().then(async (dbSessionId) => {
      try {
        // Use fetch-sumsub-data for full extraction — it has name-based search and deep OCR extraction
        const { data: fetchResult } = await supabase.functions.invoke('fetch-sumsub-data', {
          body: {
            email: ae?.email || data.email || '',
            externalUserId: personId,
            personName: ae?.name || `${ae?.firstName || ''} ${ae?.lastName || ''}`.trim(),
            sessionId: dbSessionId,
          },
        });

        const extracted = fetchResult?.extracted;
        if (extracted && (extracted.firstName || extracted.documentNumber)) {
          // Merge extracted fields into the biometrics entry and persist to DB
          const enriched = {
            ...ae,
            firstName: extracted.firstName || ae.firstName,
            lastName: extracted.lastName || ae.lastName,
            dateOfBirth: extracted.dateOfBirth,
            nationality: extracted.nationality,
            documentType: extracted.documentType,
            documentNumber: extracted.documentNumber,
            documentIssueDate: extracted.documentIssueDate,
            documentExpiry: extracted.documentExpiry,
            documentCountry: extracted.documentCountry,
            address: extracted.address,
            sumsubApplicantId: fetchResult?.applicantId,
          };
          setEnrichedEntry(enriched);

          // Also persist to DB via save-person-kyc if we have a sessionId
          if (dbSessionId && extracted.documentNumber) {
            await supabase.functions.invoke('save-person-kyc', {
              body: {
                personId,
                confirmedData: {
                  firstName: enriched.firstName,
                  lastName: enriched.lastName,
                  dateOfBirth: enriched.dateOfBirth || '',
                  email: ae?.email || data.email || '',
                  phone: ae?.phone || '',
                  nationality: enriched.nationality || '',
                  documentType: enriched.documentType || '',
                  documentNumber: enriched.documentNumber || '',
                  documentIssueDate: enriched.documentIssueDate || '',
                  documentExpiry: enriched.documentExpiry || '',
                  documentCountry: enriched.documentCountry || '',
                  street: (enriched.address as any)?.street || '',
                  city: (enriched.address as any)?.city || '',
                  postalCode: (enriched.address as any)?.postalCode || '',
                  country: (enriched.address as any)?.country || '',
                },
              },
            });
          }
        }
      } finally {
        setEnriching(false);
      }
    }).catch(() => setEnriching(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alreadyDoneInCorporate]);

  const saveExtractedData = (extractedData?: ExtractedData) => {
    if (extractedData) {
      updateData({
        extractedFirstName: extractedData.firstName,
        extractedLastName: extractedData.lastName,
        extractedDateOfBirth: extractedData.dateOfBirth,
        extractedAddress: extractedData.address,
        extractedDocumentNumber: extractedData.document?.documentNumber,
        extractedDocumentExpiry: extractedData.document?.documentExpiry,
        extractedDocumentIssueDate: extractedData.document?.documentIssueDate,
        extractedDocumentCountry: extractedData.document?.documentCountry,
        extractedNationality: extractedData.document?.nationality,
        selfieCompleted: !extractedData.verificationFailed,
        documentUploaded: true,
        poaVerified: !!extractedData.poa,
        poaDocumentType: extractedData.poa?.documentType,
        poaExtractedAddress: extractedData.poa?.address,
        poaIssueDate: extractedData.poa?.issueDate,
        poaIssuer: extractedData.poa?.issuer,
        verificationFailed: extractedData.verificationFailed,
        verificationFailureReason: extractedData.failureReason,
        verificationFailureCategory: extractedData.failureCategory,
        verificationStatus: extractedData.verificationFailed ? 'failed' : 'passed',
        biometrics: extractedData.verificationFailed && extractedData.failureCategory === 'biometrics'
          ? { liveness: { status: 'failed' as const }, faceMatch: { status: 'failed' as const } }
          : { liveness: { status: 'passed' as const }, faceMatch: { status: 'passed' as const } },
        amlScreening: extractedData.verificationFailed && extractedData.failureCategory === 'aml'
          ? {
              sanctionsMatch: extractedData.failureReason?.toLowerCase().includes('sanction') || false,
              pepMatch: extractedData.failureReason?.toLowerCase().includes('pep') || false,
              adverseMediaMatch: extractedData.failureReason?.toLowerCase().includes('media') || false,
              screeningDate: new Date().toISOString()
            }
          : undefined,
      });
    } else {
      updateData({ documentUploaded: true, selfieCompleted: true });
    }
  };

  // ── Corporate shortcut: biometrics already completed earlier in the flow ─────
  // Keep the confirmation screen, but only skip it if this person already has
  // confirmedData saved from a previous verification summary.
  
  const corporateEntry = (enrichedEntry || applicantEntry) as any;
  const corporateAddrObj = corporateEntry?.address as { street?: string; city?: string; postalCode?: string; country?: string } | undefined;
  const corporatePersonName = (
    corporateEntry?.name ||
    `${corporateEntry?.firstName || ''} ${corporateEntry?.lastName || ''}`.trim() ||
    `${data.firstName || ''} ${data.lastName || ''}`.trim()
  );
  const corporateExtracted: ExtractedData | undefined = (corporateEntry?.firstName || corporateEntry?.confirmedData?.firstName) ? {
    firstName: corporateEntry?.confirmedData?.firstName || corporateEntry?.firstName || '',
    lastName: corporateEntry?.confirmedData?.lastName || corporateEntry?.lastName || '',
    dateOfBirth: corporateEntry?.confirmedData?.dateOfBirth || corporateEntry?.dateOfBirth || '',
    address: {
      street: corporateEntry?.confirmedData?.street || corporateAddrObj?.street || '',
      city: corporateEntry?.confirmedData?.city || corporateAddrObj?.city || '',
      postalCode: corporateEntry?.confirmedData?.postalCode || corporateAddrObj?.postalCode || '',
      country: corporateEntry?.confirmedData?.country || corporateAddrObj?.country || '',
    },
    document: {
      documentType: corporateEntry?.confirmedData?.documentType || corporateEntry?.documentType || '',
      documentNumber: corporateEntry?.confirmedData?.documentNumber || corporateEntry?.documentNumber || '',
      documentIssueDate: corporateEntry?.confirmedData?.documentIssueDate || corporateEntry?.documentIssueDate || '',
      documentExpiry: corporateEntry?.confirmedData?.documentExpiry || corporateEntry?.documentExpiry || '',
      documentCountry: corporateEntry?.confirmedData?.documentCountry || corporateEntry?.documentCountry || '',
      nationality: corporateEntry?.confirmedData?.nationality || corporateEntry?.nationality || '',
    },
  } : undefined;
  const corporateInitialTaxNumber =
    corporateEntry?.confirmedData?.taxNumber ||
    ((data.corporateTaxResidencies || []) as any[]).find(
      (entry: any) => entry.name?.toLowerCase().trim() === corporatePersonName.toLowerCase().trim()
    )?.taxResidencies?.[0]?.tin ||
    '';
  

  if (alreadyDoneInCorporate) {
    if (enriching) {
      return (
        <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4">
          <Loader2 className="w-8 h-8 text-accent animate-spin" />
          <p className="text-muted-foreground text-sm">Loading your verification data…</p>
        </div>
      );
    }

    return (
      <VerificationConfirmScreen
        personName={corporatePersonName}
        email={corporateEntry?.email || data.email || ''}
        phone={corporateEntry?.phone || `${data.countryCode || ''}${data.phoneNumber || ''}`}
        extractedData={corporateExtracted}
        initialTaxNumber={corporateInitialTaxNumber}
        onConfirm={(confirmed) => {
          const updatedCorporateBiometrics = corporateBiometrics.map((entry: any) =>
            entry.id === corporateEntry?.id
              ? {
                  ...entry,
                  completed: true,
                  confirmedData: confirmed,
                  email: confirmed.email || entry.email,
                  phone: confirmed.phone || entry.phone,
                }
              : entry
          );

          updateData({ corporateBiometrics: updatedCorporateBiometrics as any });
          saveExtractedData({
            firstName: confirmed.firstName,
            lastName: confirmed.lastName,
            dateOfBirth: confirmed.dateOfBirth,
            address: { street: confirmed.street, city: confirmed.city, postalCode: confirmed.postalCode, country: confirmed.country },
            document: {
              documentType: confirmed.documentType,
              documentNumber: confirmed.documentNumber,
              documentIssueDate: confirmed.documentIssueDate,
              documentExpiry: confirmed.documentExpiry,
              documentCountry: confirmed.documentCountry,
              nationality: confirmed.nationality,
            },
          });
          goToNextStep();
        }}
      />
    );
  }

  // ── Individual flow handlers ─────────────────────────────────────────────────

  const handleSumsubComplete = (extractedData?: ExtractedData) => {
    setShowSumsub(false);
    setVerified(true);
    if (extractedData?.verificationFailed) {
      setVerificationFailed(true);
      setFailureReason(extractedData.failureReason || 'Verification could not be completed');
    }
    setPendingExtracted(extractedData);
    setShowConfirm(true);
  };

  const handleSumsubError = (error: string) => {
    console.error('Sumsub verification error:', error);
    setShowSumsub(false);
    setKycError(true);
  };

  const handleStartVerification = () => setShowSumsub(true);

  const handleContinue = () => { if (verified) goToNextStep(); };

  const handleRetry = () => {
    setKycError(false);
    setVerified(false);
    setVerificationFailed(false);
    setFailureReason(null);
    setShowConfirm(false);
    setPendingExtracted(undefined);
  };

  // After Sumsub completes (individual flow), show VerificationConfirmScreen
  if (showConfirm) {
    return (
      <VerificationConfirmScreen
        personName={`${data.firstName || ''} ${data.lastName || ''}`.trim()}
        email={data.email || ''}
        phone={`${data.countryCode || ''}${data.phoneNumber || ''}`}
        extractedData={pendingExtracted}
        onConfirm={(confirmed) => {
          saveExtractedData({
            firstName: confirmed.firstName,
            lastName: confirmed.lastName,
            dateOfBirth: confirmed.dateOfBirth,
            address: { street: confirmed.street, city: confirmed.city, postalCode: confirmed.postalCode, country: confirmed.country },
            document: {
              documentType: confirmed.documentType,
              documentNumber: confirmed.documentNumber,
              documentIssueDate: confirmed.documentIssueDate,
              documentExpiry: confirmed.documentExpiry,
              documentCountry: confirmed.documentCountry,
              nationality: confirmed.nationality,
            },
          });
          goToNextStep();
        }}
      />
    );
  }

  // When Sumsub is showing, render it in the standard page layout
  if (showSumsub && !verified && !kycError) {
    return (
      <div className="min-h-screen bg-background flex flex-col pt-24 pb-28 px-4 sm:px-6">
        <div className="flex-1 flex flex-col max-w-md mx-auto w-full">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-foreground mb-2">
              Verify your identity
            </h1>
            <p className="text-muted-foreground">
              Complete identity verification including document, biometrics, and proof of address in one secure session.
            </p>
          </div>
          <SumsubVerification
            onComplete={handleSumsubComplete}
            onError={handleSumsubError}
            email={data.email}
            phone={`${data.countryCode}${data.phoneNumber}`}
            includePOA={true}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col pt-24 pb-28 px-4 sm:px-6">
      <div className="flex-1 flex flex-col max-w-md mx-auto w-full overflow-y-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-foreground mb-2">
            Verify your identity
          </h1>
          <p className="text-muted-foreground">
            Complete identity verification including document, biometrics, and proof of address in one secure session.
          </p>
        </div>

        {/* Start Verification UI */}
        {!verified && !kycError && (
          <div className="animate-fade-in-up">
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Complete all verification steps in one secure session:
              </p>
              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-3 p-3 bg-secondary/50 rounded-lg">
                  <FileText className="w-5 h-5 text-accent" />
                  <div>
                    <p className="text-sm font-medium text-foreground">ID Document</p>
                    <p className="text-xs text-muted-foreground">Passport, ID card, or driving license</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-secondary/50 rounded-lg">
                  <Camera className="w-5 h-5 text-accent" />
                  <div>
                    <p className="text-sm font-medium text-foreground">Biometric Verification</p>
                    <p className="text-xs text-muted-foreground">Liveness check and face match</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-secondary/50 rounded-lg">
                  <Home className="w-5 h-5 text-accent" />
                  <div>
                    <p className="text-sm font-medium text-foreground">Proof of Address</p>
                    <p className="text-xs text-muted-foreground">Bank statement, utility bill, or tax document</p>
                  </div>
                </div>
              </div>
              <button
                onClick={handleStartVerification}
                className="w-full border-2 border-dashed border-accent/50 rounded-xl p-6 text-center hover:border-accent hover:bg-accent/5 transition-all cursor-pointer"
              >
                <Shield className="w-12 h-12 text-accent mx-auto mb-3" />
                <p className="font-medium text-foreground mb-1">Start Identity Verification</p>
                <p className="text-sm text-muted-foreground">ID + Biometrics + Proof of Address</p>
              </button>
              <div className="flex items-center gap-2 text-xs text-muted-foreground justify-center">
                <Shield className="w-3 h-3" />
                <span>Powered by Sumsub • Bank-grade security</span>
              </div>
            </div>
          </div>
        )}

        {/* KYC Error State */}
        {kycError && (
          <div className="space-y-4 animate-fade-in-up">
            <div className="border-2 border-destructive/30 rounded-xl p-6 text-center bg-destructive/5">
              <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-3" />
              <p className="font-medium text-foreground mb-1">Verification Unavailable</p>
              <p className="text-sm text-muted-foreground mb-4">
                Our verification service is temporarily unavailable. Please try again.
              </p>
              <button
                onClick={handleRetry}
                className="w-full py-2 px-4 bg-accent text-accent-foreground font-medium rounded-lg hover:bg-accent/90 transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        )}

        {/* Success State */}
        {verified && !verificationFailed && (
          <div className="animate-fade-in-up">
            <div className="border-2 border-success rounded-xl p-6 text-center bg-success/5">
              <div className="w-12 h-12 rounded-full bg-success/20 flex items-center justify-center mx-auto mb-3">
                <Check className="w-6 h-6 text-success" />
              </div>
              <p className="font-medium text-foreground">Identity verified!</p>
              <p className="text-sm text-muted-foreground">Your identity has been verified successfully</p>
            </div>
          </div>
        )}

        {/* Verification Failed State */}
        {verified && verificationFailed && (
          <div className="animate-fade-in-up">
            <div className="border-2 border-yellow-500 rounded-xl p-6 text-center bg-yellow-50 dark:bg-yellow-950/20">
              <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center mx-auto mb-3">
                <AlertTriangle className="w-6 h-6 text-yellow-600" />
              </div>
              <p className="font-medium text-foreground mb-2">Verification Requires Review</p>
              <p className="text-sm text-muted-foreground mb-4">{failureReason}</p>
              <div className="p-4 bg-background rounded-lg border border-border">
                <p className="text-sm text-foreground font-medium mb-1">What happens next?</p>
                <p className="text-xs text-muted-foreground">
                  You can continue with your application. Our support team will review your case and may send you a new verification link if needed.
                </p>
              </div>
            </div>
          </div>
        )}

      </div>
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-background border-t border-border">
        <div className="max-w-md mx-auto">
          <button
            onClick={handleContinue}
            disabled={!verified}
            className="w-full h-14 text-lg font-semibold rounded-xl bg-accent hover:bg-accent/90 text-accent-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}
